import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header3 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template3' },
        { label: 'Shop', path: '/template3/shop' },
        { label: 'About', path: '/template3/about' },
        { label: 'Contact', path: '/template3/contact' },
        { label: 'Orders', path: '/template3/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');
                .vastra-desktop-nav { display: none; }
                @media (min-width: 1024px) {
                    .vastra-desktop-nav { display: flex; }
                    .vastra-mobile-toggle { display: none !important; }
                }
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(255, 253, 247, 0.98)' : 'rgba(255, 253, 247, 0.95)',
                    borderBottom: scrolled ? '1px solid #E8E0D8' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(12px)',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '76px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template3')}
                        style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: '26px',
                            fontWeight: 700,
                            color: '#722F37',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                        }}
                    >
                        VASTRA
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '36px',
                        }}
                        className="vastra-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Playfair Display', serif",
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    color: '#1A1A1A',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#722F37')}
                                onMouseLeave={(e) => (e.target.style.color = '#1A1A1A')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#722F37')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <User
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template3/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#722F37')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template3/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#1A1A1A', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#722F37')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        width: '18px',
                                        height: '18px',
                                        backgroundColor: '#722F37',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: "'Lato', sans-serif",
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Menu Toggle */}
                        <button
                            className="vastra-mobile-toggle"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#1A1A1A',
                                padding: '4px',
                            }}
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 49,
                        backgroundColor: 'rgba(255, 253, 247, 0.99)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '32px',
                    }}
                >
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontSize: '24px',
                                fontWeight: 500,
                                color: '#1A1A1A',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#722F37')}
                            onMouseLeave={(e) => (e.target.style.color = '#1A1A1A')}
                        >
                            {link.label}
                        </button>
                    ))}
                    <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
                        <button
                            onClick={() => handleNavClick('/template3/login')}
                            style={{
                                fontFamily: "'Lato', sans-serif",
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#FFFFFF',
                                backgroundColor: '#722F37',
                                border: '1px solid #722F37',
                                padding: '12px 32px',
                                cursor: 'pointer',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                            }}
                        >
                            Login
                        </button>
                        <button
                            onClick={() => handleNavClick('/template3/signup')}
                            style={{
                                fontFamily: "'Lato', sans-serif",
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#722F37',
                                backgroundColor: 'transparent',
                                border: '1px solid #722F37',
                                padding: '12px 32px',
                                cursor: 'pointer',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                            }}
                        >
                            Sign Up
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header3;
